// @givmo/org-console — the AUTOMATIC CONNECTION setting's words (Givmo MCP MCP-P17b, D-MCP-106).
//
// ⚠ EVERY WORD HERE IS A DRAFT for Jonathan's copy round (MCP-P17g, row 7 of the EMA build): approved
// on a rendered screen and tried on dev first, never shipped past dev on this file's say-so. They are
// kept in ONE place so that round edits one file.
//
// What the words may and may not say (D-MCP-106; design-16 §2): a Global Admin's approval stands in
// for each person's own consent; people are matched only to accounts that exist and may already
// connect an AI here; every call keeps the person's own access and is recorded as them; each person
// is told and can turn it off. Never that anyone is given access, and never a claim about how
// strongly a person's identity was proven: the setting checks the issuer, not the person.

import type { StepUpCopyTable } from '@givmo/step-up';
import type {
  AutomaticConnectionKind,
  AutomaticConnectionProblem,
} from './org-automatic-connection-wire';

/** The step-up action the backend names (`app/domain/org_trusted_issuer.py`). */
export const AUTOMATIC_CONNECTION_STEP_UP_ACTION = 'set_automatic_connection';

/** The rows a session held to the emailed code's limits opens (AS-PWL-2), one per cure. */
export const AUTOMATIC_CONNECTION_REAUTH_ROW = 'automatic-connection-code-session-reauth';
export const AUTOMATIC_CONNECTION_ADD_PASSKEY_ROW = 'automatic-connection-code-session-add-passkey';
export const AUTOMATIC_CONNECTION_PASSKEY_ROW = 'automatic-connection-code-session-passkey';

export const AUTOMATIC_CONNECTION_COPY = {
  title: 'Automatic connection',
  offLine: 'Off. People here connect an AI by approving it themselves.',
  onLine: (source: string): string => `On. People here can be connected through ${source}.`,
  setBy: (name: string, when: string): string =>
    name.length > 0 ? `Set by ${name}, ${when}` : `Set ${when}`,
  setUp: 'Set up',
  change: 'Change',
  turnOff: 'Turn off',
  turnedOffForHeading: 'Turned off for',
  turnedOffForEmpty: 'No one has turned it off.',
  loadFailed: 'We couldn’t load automatic connection.',
  tryAgain: 'Try again',

  dialogTitle: 'Set up automatic connection',
  changeDialogTitle: 'Change automatic connection',
  dialogIntro: 'Paste the issuer URL from Claude or your identity provider.',
  issuerLabel: 'Issuer URL',
  issuerPlaceholder: 'https://identity.anthropic.com/issuers/…',
  organizationIdLabel: 'Tenant ID for Claude (optional)',
  copy: 'Copy',
  copied: 'Copied',
  usesTenantId: 'I entered this ID in Claude',
  check: 'Check',
  checking: 'Checking…',
  keysFrom: (host: string): string => `Keys from ${host}`,
  disclosureLead: 'When it’s on:',
  disclosure: (source: string): readonly string[] => [
    `People here who can connect an AI are connected through ${source} without being asked.`,
    // The approved organization block (design-16 §2, row 7), said of the people here. This package
    // never learns the organization's class, so it names no portal.
    'Their AI can work within the Givmo apps your organization has turned on and they have access to. It can’t sign anything or move money.',
    'Everything their AI does is recorded as them, with their own access.',
    'Each person is told, and can turn it off.',
  ],
  turnOn: 'Turn on',
  save: 'Save',
  cancel: 'Cancel',
  changed: 'This changed since you checked it. Review it again.',
  /** Beside `changed`: what is stored now, from the refusal's fresh render. */
  changedStored: (issuer: string, name: string): string =>
    name.length > 0
      ? `Right now it trusts ${issuer}, set by ${name}.`
      : `Right now it trusts ${issuer}.`,
  changedStoredOff: 'Right now it’s off.',
  saveFailed: 'We couldn’t save this. Nothing changed. Try again.',
  checkFailed: 'We couldn’t check this issuer. Try again.',

  turnOffTitle: 'Turn off automatic connection?',
  turnOffBody: 'People here will connect an AI by approving it themselves.',
  turnOffFailed: 'We couldn’t turn this off. Try again.',

  // A session held to the emailed code's limits, no passkey on the account, on a host where one can
  // be made (MCP-P6d). What to do while the offer is up; who else can after "Not now", when the same
  // button opens the offer again; and the last where nothing can be set up (no WebAuthn in this
  // browser, passkeys dark here, a lock on the account: the same words for all three, so none is
  // false). `changing`: the act is a change to a stored issuer, not turning it on.
  setUpPasskey: 'Set up a passkey',
  setUpPasskeyThen: (changing: boolean): string =>
    `Set up a passkey on this device, then ${changing ? 'change this' : 'turn this on'}.`,
  needsPasskey: (changing: boolean): string =>
    changing
      ? 'Changing this needs a passkey on your account. Another Global Admin can change it.'
      : 'Turning this on needs a passkey on your account. Another Global Admin can turn it on.',
  passkeyCantBeMade: (changing: boolean): string =>
    changing
      ? 'Changing this needs a passkey, and you can’t set one up right now. Another Global Admin can change it.'
      : 'Turning this on needs a passkey, and you can’t set one up right now. Another Global Admin can turn it on.',
} as const;

/** The Activity page's headlines for an `AUTOMATIC_CONNECTION_CHANGED` row. */
export const AUTOMATIC_CONNECTION_EVENT_COPY = {
  changed: 'Automatic connection changed',
  on: 'Automatic connection turned on',
  off: 'Automatic connection turned off',
} as const;

/** What an issuer's kind reads as, in a sentence ("…connected through Claude"). */
export function automaticConnectionSource(kind: AutomaticConnectionKind | null): string {
  return kind === 'claude' ? 'Claude' : 'your identity provider';
}

/** What an issuer's kind reads as, as a label beside the address. */
export function automaticConnectionKindLabel(kind: AutomaticConnectionKind | null): string {
  return kind === 'claude' ? 'Claude' : 'Identity provider';
}

/** Why a checked issuer can't be used, in one line. */
export function automaticConnectionProblemLine(problem: AutomaticConnectionProblem): string {
  switch (problem) {
    case 'issuer_invalid':
      return 'Enter the issuer URL exactly as it’s shown, starting with https://.';
    case 'issuer_mismatch':
      return 'This URL doesn’t match the issuer it points to. Copy it again exactly, including any slash at the end.';
    case 'issuer_no_keys':
      return 'This issuer doesn’t publish signing keys, so Givmo can’t use it.';
    case 'issuer_held_elsewhere':
      return 'Another organization on Givmo uses this issuer. Enter the Tenant ID above in Claude, then tick the box.';
    case 'issuer_unreachable':
    default:
      return 'Givmo couldn’t reach this issuer. Check the URL and try again.';
  }
}

/**
 * The "Confirm it's you" sheet's rows for this act (`@givmo/step-up`'s action-keyed rows).
 * `passkeysWorkHere`: the two lines that say this page can't set up or use a passkey are true only on
 * a host where no passkey ceremony can run (MCP-P6d, `org-first-passkey.tsx`).
 * `changing`: the act is a change to a stored issuer, so no line says "turn on".
 */
export function automaticConnectionStepUpCopy(
  passkeysWorkHere: boolean,
  changing: boolean,
): StepUpCopyTable {
  const what = changing ? 'change automatic connection' : 'turn on automatic connection';
  const doing = changing ? 'Changing this' : 'Turning this on';
  const another = changing
    ? 'Another Global Admin can change it.'
    : 'Another Global Admin can turn it on.';
  const row = {
    description: () => `You signed in a while ago. To ${what}, confirm it’s you.`,
    consequence: 'Nothing changes until you confirm.',
    challengeAction: what,
    unavailable: 'We couldn’t confirm it’s you here. Sign out, sign in again, then try again.',
  };
  const codeSession = {
    ...row,
    description: () => `A sign-in with a code we email can’t be used to ${what}.`,
  };
  return {
    [AUTOMATIC_CONNECTION_STEP_UP_ACTION]: row,
    [AUTOMATIC_CONNECTION_REAUTH_ROW]: {
      ...codeSession,
      description: () =>
        `A sign-in with a code we email can’t be used to ${what}. Confirm it’s you another way.`,
      unavailable: `${doing} needs a sign-in another way, and this page doesn’t offer yours. ${another}`,
    },
    [AUTOMATIC_CONNECTION_ADD_PASSKEY_ROW]: passkeysWorkHere
      ? codeSession
      : {
          ...codeSession,
          unavailable: `${doing} needs a passkey on your account, and this page can’t set one up yet. ${another}`,
        },
    [AUTOMATIC_CONNECTION_PASSKEY_ROW]: {
      ...codeSession,
      description: () =>
        `After a sign-in with a code we emailed, only your passkey can be used to ${what}.`,
      ...(passkeysWorkHere
        ? {}
        : {
            passkeyUnavailable: `${doing} needs your passkey, and this page can’t use one yet. ${another}`,
          }),
    },
  };
}
